import opentype, { Font } from "opentype.js";

import { FontCastledownTTF, FontSatoshiBoldWOFF } from "src/static/fonts";

/**
 * Browser canvas fillText uses each engine's font stack (Core Text vs Skia),
 * so Safari and Chrome disagree on glyph size/position. Drawing OpenType
 * paths from the same font files keeps preview + export identical everywhere.
 */

let headingFont: Font | null = null;
let inkFont: Font | null = null;
let loadPromise: Promise<void> | null = null;

const loadFont = (url: string): Promise<Font> =>
  new Promise((resolve, reject) => {
    opentype.load(url, (err, font) => {
      if (err || !font) {
        reject(err || new Error(`Failed to load font: ${url}`));
        return;
      }
      resolve(font);
    });
  });

export const loadGraphicFonts = (): Promise<void> => {
  if (!loadPromise) {
    loadPromise = Promise.all([
      loadFont(FontCastledownTTF),
      loadFont(FontSatoshiBoldWOFF),
    ]).then(([heading, ink]) => {
      headingFont = heading;
      inkFont = ink;
    });
  }
  return loadPromise;
};

export const getHeadingFont = (): Font => {
  if (!headingFont) {
    throw new Error(
      "Graphic heading font not loaded — call loadGraphicFonts()"
    );
  }
  return headingFont;
};

export const getInkFont = (): Font => {
  if (!inkFont) {
    throw new Error("Graphic ink font not loaded — call loadGraphicFonts()");
  }
  return inkFont;
};

export const measureFontText = (
  font: Font,
  text: string,
  fontSize: number
): number => font.getAdvanceWidth(text, fontSize);

/** Distance from text-box top to alphabetic baseline (matches canvas top ≈ em top). */
export const baselineFromTop = (font: Font, fontSize: number): number =>
  (font.ascender / font.unitsPerEm) * fontSize;

/** Alphabetic baseline Y so the em-box center sits at 0 (canvas middle). */
export const baselineForMiddle = (font: Font, fontSize: number): number =>
  ((font.ascender + font.descender) / (2 * font.unitsPerEm)) * fontSize;

const fillPath = (
  ctx: CanvasRenderingContext2D,
  font: Font,
  text: string,
  x: number,
  baselineY: number,
  fontSize: number,
  fillStyle: string | CanvasGradient
) => {
  const path = font.getPath(text, x, baselineY, fontSize);
  ctx.fillStyle = fillStyle;
  ctx.fill(new Path2D(path.toPathData(2)));
};

/** Top-left / top-center aligned text (same role as textBaseline:"top"). */
export const fillFontTextTop = (
  ctx: CanvasRenderingContext2D,
  font: Font,
  text: string,
  x: number,
  topY: number,
  fontSize: number,
  align: "left" | "center",
  fillStyle: string | CanvasGradient
) => {
  const width = measureFontText(font, text, fontSize);
  const left = align === "center" ? x - width / 2 : x;
  const baselineY = topY + baselineFromTop(font, fontSize);
  fillPath(ctx, font, text, left, baselineY, fontSize, fillStyle);
};

/** Centered glyph at (0, 0) — for circular ink-stamp letters. */
export const fillFontCharCentered = (
  ctx: CanvasRenderingContext2D,
  font: Font,
  char: string,
  fontSize: number,
  fillStyle: string | CanvasGradient
) => {
  const width = measureFontText(font, char, fontSize);
  const baselineY = baselineForMiddle(font, fontSize);
  fillPath(ctx, font, char, -width / 2, baselineY, fontSize, fillStyle);
};
